import { BufferGeometry, Float32BufferAttribute, type Matrix4, type Plane, Vector3 } from "three";

type ClipVertex = {
  normal: Vector3;
  position: Vector3;
};

type GeometryGroup = {
  count: number;
  materialIndex: number;
  start: number;
};

const clipEpsilon = 1e-6;
const supportedAttributeNames = new Set(["normal", "position"]);

export function clipMeshGeometry(
  geometry: BufferGeometry,
  worldPlanes: Plane[],
  matrixWorld: Matrix4
) {
  validateGeometry(geometry);

  const source = geometry.index ? geometry.toNonIndexed() : geometry.clone();
  const positions = source.getAttribute("position");
  const normals = source.getAttribute("normal");
  const localMatrix = matrixWorld.clone().invert();
  const localPlanes = worldPlanes.map((plane) => plane.clone().applyMatrix4(localMatrix));
  const outputPositions: number[] = [];
  const outputNormals: number[] = [];
  const outputGroups: GeometryGroup[] = [];

  for (let triangleStart = 0; triangleStart < positions.count; triangleStart += 3) {
    let polygon = [0, 1, 2].map((offset) => ({
      normal: new Vector3().fromBufferAttribute(normals, triangleStart + offset),
      position: new Vector3().fromBufferAttribute(positions, triangleStart + offset)
    }));

    for (const plane of localPlanes) {
      polygon = clipPolygon(polygon, plane);
      if (polygon.length < 3) {
        break;
      }
    }

    const materialIndex = getTriangleMaterialIndex(source.groups, triangleStart);
    for (let index = 1; index < polygon.length - 1; index += 1) {
      const triangle = [polygon[0], polygon[index], polygon[index + 1]];
      if (isDegenerateTriangle(triangle)) {
        continue;
      }

      appendGroup(outputGroups, materialIndex, outputPositions.length / 3);
      triangle.forEach((vertex) => {
        outputPositions.push(vertex.position.x, vertex.position.y, vertex.position.z);
        vertex.normal.normalize();
        outputNormals.push(vertex.normal.x, vertex.normal.y, vertex.normal.z);
      });
    }
  }

  source.dispose();

  const clipped = new BufferGeometry();
  clipped.name = geometry.name;
  clipped.setAttribute("position", new Float32BufferAttribute(outputPositions, 3));
  clipped.setAttribute("normal", new Float32BufferAttribute(outputNormals, 3));
  outputGroups.forEach((group) => {
    clipped.addGroup(group.start, group.count, group.materialIndex);
  });
  clipped.computeBoundingBox();
  clipped.computeBoundingSphere();
  return clipped;
}

function validateGeometry(geometry: BufferGeometry) {
  if (!geometry.getAttribute("position") || !geometry.getAttribute("normal")) {
    throw new Error("裁切模型必须包含 POSITION 和 NORMAL 顶点属性");
  }

  const unsupportedAttributes = Object.keys(geometry.attributes).filter(
    (name) => !supportedAttributeNames.has(name)
  );
  if (unsupportedAttributes.length > 0 || Object.keys(geometry.morphAttributes).length > 0) {
    throw new Error("裁切模型包含暂不支持的顶点属性");
  }
}

function clipPolygon(vertices: ClipVertex[], plane: Plane) {
  const clipped: ClipVertex[] = [];

  for (let index = 0; index < vertices.length; index += 1) {
    const current = vertices[index];
    const previous = vertices[(index + vertices.length - 1) % vertices.length];
    const currentDistance = plane.distanceToPoint(current.position);
    const previousDistance = plane.distanceToPoint(previous.position);
    const currentInside = currentDistance >= -clipEpsilon;
    const previousInside = previousDistance >= -clipEpsilon;

    if (currentInside !== previousInside) {
      clipped.push(interpolateVertex(previous, current, previousDistance, currentDistance));
    }
    if (currentInside) {
      clipped.push(current);
    }
  }

  return clipped;
}

function interpolateVertex(
  start: ClipVertex,
  end: ClipVertex,
  startDistance: number,
  endDistance: number
) {
  const denominator = startDistance - endDistance;
  const alpha = Math.abs(denominator) <= clipEpsilon ? 0.5 : startDistance / denominator;
  return {
    normal: start.normal.clone().lerp(end.normal, alpha).normalize(),
    position: start.position.clone().lerp(end.position, alpha)
  };
}

function isDegenerateTriangle([first, second, third]: ClipVertex[]) {
  const firstEdge = second.position.clone().sub(first.position);
  const secondEdge = third.position.clone().sub(first.position);
  return firstEdge.cross(secondEdge).lengthSq() <= clipEpsilon * clipEpsilon;
}

function getTriangleMaterialIndex(groups: BufferGeometry["groups"], triangleStart: number) {
  return (
    groups.find(
      (group) => triangleStart >= group.start && triangleStart < group.start + group.count
    )?.materialIndex ?? 0
  );
}

function appendGroup(groups: GeometryGroup[], materialIndex: number, triangleStart: number) {
  const current = groups.at(-1);
  if (current?.materialIndex === materialIndex && current.start + current.count === triangleStart) {
    current.count += 3;
    return;
  }
  groups.push({ count: 3, materialIndex, start: triangleStart });
}
